/**
 * EventGuestsSection
 *
 * Email invitees/guests section for the event modal.
 * Shows "Invited" title with pills for non-family-linked email invitees
 * and a collapsible email input. Calendar-linked family members are
 * managed via the "For" section instead.
 */

import { useMemo, useState } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { EventParticipant } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

type EventGuestsSectionProps = {
  participants: EventParticipant[];
  onAddEmail: (email: string) => void;
  onRemoveParticipant: (id: string) => void;
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  calendars: DeviceCalendar[];
  palette: typeof Colors.light;
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function EventGuestsSection({
  participants,
  onAddEmail,
  onRemoveParticipant,
  familyMembers,
  calendarMappings,
  calendars,
  palette,
}: EventGuestsSectionProps) {
  const [emailInput, setEmailInput] = useState('');
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  // Filter out invitees that match calendar-linked family members
  // (those are managed via the "For" section, not "Invited")
  const invitedGuests = useMemo(() => {
    const allInvited = participants.filter(p => p.role === 'invited');
    return allInvited.filter(guest => {
      const matched = resolveInviteeToFamilyMember(
        guest,
        calendars,
        calendarMappings,
        familyMembers,
      );
      return matched === null; // Only show non-family-linked invitees
    });
  }, [participants, calendars, calendarMappings, familyMembers]);

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && isValidEmail(trimmed)) {
      onAddEmail(trimmed);
      setEmailInput('');
      setIsInputExpanded(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons name="people-outline" size={18} color={palette.textMuted} />
      </View>
      <View style={styles.content}>
        {/* Section title */}
        <Text style={[styles.sectionTitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          Invited
        </Text>

        {/* Invitee pills (only non-family-linked emails) */}
        {invitedGuests.length > 0 && (
          <View style={styles.pillsContainer}>
            {invitedGuests.map(guest => (
              <InviteePill
                key={guest.id}
                guest={guest}
                onRemove={onRemoveParticipant}
                tintColor={palette.tint}
                familyMembers={familyMembers}
                calendarMappings={calendarMappings}
                calendars={calendars}
              />
            ))}
          </View>
        )}

        {/* Email input — collapsed or expanded */}
        {isInputExpanded ? (
          <View style={styles.inputRow}>
            <TextInput
              style={[styles.input, { color: palette.text, fontFamily: getBodyFont() }]}
              value={emailInput}
              onChangeText={setEmailInput}
              placeholder="Enter email address"
              placeholderTextColor={palette.textMuted}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              onSubmitEditing={handleAddEmail}
              returnKeyType="done"
              autoFocus
              onBlur={() => {
                if (!emailInput.trim()) setIsInputExpanded(false);
              }}
            />
            {emailInput.trim() && isValidEmail(emailInput.trim()) && (
              <TouchableOpacity onPress={handleAddEmail} style={styles.addIconButton}>
                <Ionicons name="add-circle" size={20} color={palette.tint} />
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <TouchableOpacity
            onPress={() => setIsInputExpanded(true)}
            style={styles.addMoreButton}
          >
            <Ionicons name="add" size={16} color={palette.tint} />
            <Text style={[styles.addMoreLabel, { color: palette.tint, fontFamily: getBodyFont() }]}>
              Invite others via email...
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

// --- Invitee Pill component ---

type InviteePillProps = {
  guest: EventParticipant;
  onRemove: (id: string) => void;
  tintColor: string;
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  calendars: DeviceCalendar[];
};

function InviteePill({ guest, onRemove, tintColor, familyMembers, calendarMappings, calendars }: InviteePillProps) {
  const backgroundColor = tintColor;
  const linkedMember = resolveInviteeToFamilyMember(guest, calendars, calendarMappings, familyMembers);
  const displayText = linkedMember?.name ?? guest.name ?? guest.email ?? 'Unknown';

  return (
    <View style={[styles.pill, { backgroundColor, borderColor: backgroundColor }]}>
      <Text
        style={[styles.pillText, { fontFamily: getBodyFont() }]}
        numberOfLines={1}
      >
        {displayText}
      </Text>
      {!guest.isSelf && (
        <TouchableOpacity onPress={() => onRemove(guest.id)}>
          <Ionicons name="close-circle" size={16} color="rgba(255,255,255,0.8)" />
        </TouchableOpacity>
      )}
    </View>
  );
}

// --- Resolution helper ---

function resolveInviteeToFamilyMember(
  guest: EventParticipant,
  calendars: DeviceCalendar[],
  calendarMappings: CalendarMemberMapping[],
  familyMembers: FamilyMember[],
): FamilyMember | null {
  const guestEmail = guest.email?.toLowerCase();
  if (!guestEmail) return null;

  // Find calendars whose ownerAccount matches this invitee's email
  const matchingCalendarIds = calendars
    .filter(cal => cal.ownerAccount?.toLowerCase() === guestEmail)
    .map(cal => cal.id);

  if (matchingCalendarIds.length === 0) return null;

  // Check if any of those calendars are mapped to a family member
  for (const calId of matchingCalendarIds) {
    const mapping = calendarMappings.find(m => m.calendarId === calId);
    if (mapping && mapping.memberIds.length > 0) {
      const member = familyMembers.find(m => m.id === mapping.memberIds[0]);
      if (member) return member;
    }
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2,
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    paddingTop: Spacing.xs,
  },
  content: {
    flex: 1,
    gap: Spacing.sm,
  },
  sectionTitle: {
    fontSize: 14,
  },
  pillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.lg,
    borderWidth: 1.5,
    gap: Spacing.sm,
  },
  pillText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    flexShrink: 1,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    fontSize: 14,
    paddingVertical: Spacing.xs,
  },
  addIconButton: {
    padding: Spacing.xs,
  },
  addMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    paddingVertical: Spacing.xs,
  },
  addMoreLabel: {
    fontSize: 14,
  },
});
